<script setup>
import { ref, onMounted } from "vue";
import { useModals } from "@/utils/useModals";
import { useScrollStore } from "@/stores/storeScroll";
import { useSettingsStore } from "@/stores/storeSettings";
import { rulesUrl } from "@/utils/provider";
import { sendMetrik } from "@/utils/metrika";

const { openModal } = useModals();
const storeScroll = useScrollStore();
const storeSettings = useSettingsStore();

const faqActive = ref({});
const dataFaq = ref([]);
const smoother = ref(null);

const formatText = (text) => {
	let formattedText = text.replace(/\r?\n/g, "<br>");

	const urlPattern = /(?<!<a href=")(https?:\/\/[^\s]+)(?!")/g;

	formattedText = formattedText.replace(urlPattern, '<a href="$1" target="_blank">$1</a>');

	return formattedText;
};

const formatAnswer = (text) => {
	if (!text) return "";
	const escaped = text.replace(/(?:\r\n|\r|\n)/g, "<br>").replace("Правил по ссылке", `<a href="${rulesUrl}" target="_blank" rel="noopener noreferrer">ссылке</a>`);
	return escaped;
};

const openFaqItem = (index) => {
	const newActiveState = {};
	for (let i = 0; i < dataFaq.value.length; i++) {
		newActiveState[i] = i === index ? !faqActive.value[index] : false;
	}
	faqActive.value = newActiveState;
};

onMounted(async () => {
	dataFaq.value = await storeSettings.getFaq();
});
</script>
<template>
	<section class="faq section" id="faq">
		<div class="container">
			<h2 class="faq__title title2">FAQ</h2>
			<div class="faq__container">
				<ul v-if="dataFaq" class="faq__list list-scrollbar">
					<li :class="['faq__item', { active: faqActive[index] }]" data-accordion-parent v-for="(faq, index) in dataFaq" :key="index" @click="openFaqItem(index)">
						<div class="faq__question">
							{{ faq.question }}
							<div class="faq__btn">
								<svg width="24" height="24"><use href="#prev"></use></svg>
							</div>
						</div>
						<div class="faq__answer" data-accordion-hidden>
							<div style="min-height: 0" v-html="formatAnswer(faq.answer)"></div>
						</div>
					</li>
				</ul>
				<div class="faq__block" :style="{ top: storeScroll.header?.offsetHeight + 20 + 'px' }">
					<h3 class="faq__block-title">Если у вас возникли какие-либо вопросы, напишите нам!</h3>
					<button class="faq__feedback-btn btn --blue" @click="openModal('feedback'), sendMetrik('reachGoal', 'klik_button_ask_question')">Написать</button>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
@use "@/assets/scss/blocks/accordion-grid.scss";
.faq {
	position: relative;
	background-image: url('@/assets/img/faq_bg.webp');
	background-repeat: no-repeat;
	background-position: 0% 90%; /* x y*/
	background-size: 14%;
	@media (max-width: vars.$lg) {
		background-size: 10%;
	}
	@media (max-width: vars.$l) {
		background-image: none;
	}
}

.faq__container {
	position: relative;
	display: grid;
	grid-template-columns: 1fr 42.3rem;
	align-items: start;
	gap: 4rem;
	z-index: 1;
	@media (max-width: vars.$mn) {
		grid-template-columns: 1fr 35rem;
	}
	@media (max-width: vars.$xx) {
		grid-template-columns: 1fr;
		gap: 3rem;
	}
}

.faq__list {
	display: grid;
	gap: 2.1rem;
	@media (max-width: vars.$x480) {
	}
}
.faq__item {
	padding: 4.3rem 4rem 3.8rem;
	transition: var(--transition) ease-in;
	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: var(--border-radius);
	@media (max-width: vars.$t) {
	}
	@media (max-width: vars.$x480) {
		padding: 24px 16px;
	}
	&.active {
		border-color: #0c4ca3;
		.faq__btn {
			transform: rotate(135deg);
		}
		.faq__answer {
			padding-top: 1.4rem;
			@media (max-width: vars.$xx) {
				padding-top: 1rem;
			}
		}
	}
}
.faq__question {
	width: 100%;
	display: flex;
	align-items: start;
	justify-content: space-between;
	gap: 2.4rem;
	font-weight: 500;
	font-size: 2.4rem;
	line-height: 140%;
	color: #0c4ca3;
	cursor: pointer;
	@media (max-width: vars.$x480) {
		font-size: 1.6rem;
		gap: 1.5rem;
	}
	@media (hover: hover) {
		&:hover {
			.faq__btn {
			}
		}
	}
}
.faq__btn {
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--fill-inverse-1);
	flex-shrink: 0;
	width: 2.4rem;
	height: 2.4rem;
	transform: rotate(-45deg);
	transition: var(--transition) ease-in;
	@media (max-width: vars.$x480) {
	}
	svg {
		width: 100%;
		height: 100%;
	}
}
.faq__answer {
	font-weight: 400;
	font-size: 2rem;
	line-height: 160%;
	@media (max-width: vars.$x480) {
		font-size: 1.6rem;
	}
	a {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}

	span {
		font-family: var(--third-family);
	}
	:deep(span) {
	}
}

.faq__block {
	position: sticky;
	z-index: 1;
	border-radius: var(--border-radius);
	border: 1px solid var(--border-inverse-3);
	padding: 3.2rem 3.2rem;
	@media (max-width: vars.$x480) {
		padding: 3.3rem 3.1rem;
	}
}
.faq__block-title {
	font-weight: 500;
	font-size: 3.2rem;
	line-height: 120%;
	color: var(--text-accent-1-100);
	margin-bottom: 2.3rem;
	@media (max-width: vars.$x480) {
		font-size: 2.4rem;
		margin-bottom: 3rem;
	}
}
.faq__feedback-btn {
	width: 100%;
	@media (max-width: vars.$x480) {
	}
}
</style>
